import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton, Stack, Typography } from "@mui/material";
import { GridPillChip, PivotTab } from "design-components";
import { useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { OperatorDetailCustomers } from "../components/operator-detail-customers.component";
import { OperatorDetailLocations } from "../components/operator-detail-locations.component";
import { OperatorDetailOverview } from "../components/operator-detail-overview.component";
import type { OperatorDetail, OperatorDetailTab } from "../types";

const OPERATOR_DETAIL_TAB_QUERY_KEY = "tab";

const OPERATOR_DETAILS: Record<string, OperatorDetail> = {
  "operator-row-1": {
    id: "operator-row-1",
    companyName: "Con Global",
    status: "active",
    companyLegalName: "Con Global",
    primaryContact: "Evan Kingston",
    phone: "+1.555.123.1234",
    email: "evan.kingston@conglobal.com",
    users: [
      { id: "user-1", fullName: "Jennifer Stockton", role: "Admin", lastSeenLabel: "Last seen today" },
      { id: "user-2", fullName: "Tim Proper", role: "Admin", lastSeenLabel: "Last seen yesterday" },
      { id: "user-3", fullName: "Jake Miller", role: "Support", lastSeenLabel: "Last seen today" },
      { id: "user-4", fullName: "Devin Holster", role: "Support", lastSeenLabel: "Last seen Apr 1" },
      { id: "user-5", fullName: "Maya Lin", role: "Admin", lastSeenLabel: "Last seen Mar 31" },
      { id: "user-6", fullName: "Jaxon Lee", role: "Support", lastSeenLabel: "Last login yesterday" },
    ],
  },
};

const DETAIL_TABS: ReadonlyArray<{ id: OperatorDetailTab; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "locations", label: "Locations" },
  { id: "customers", label: "Customers" },
];

export function OperatorDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = parseTabParam(searchParams.get(OPERATOR_DETAIL_TAB_QUERY_KEY));
  const operator = useMemo(() => {
    if (id && OPERATOR_DETAILS[id]) {
      return OPERATOR_DETAILS[id];
    }
    return OPERATOR_DETAILS["operator-row-1"];
  }, [id]);

  const handleTabChange = (tab: OperatorDetailTab) => {
    setSearchParams(
      (previousParams) => {
        const next = new URLSearchParams(previousParams);
        if (tab === "overview") {
          next.delete(OPERATOR_DETAIL_TAB_QUERY_KEY);
        } else {
          next.set(OPERATOR_DETAIL_TAB_QUERY_KEY, tab);
        }
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="settings-page-shell operator-detail-page">
      <div className="settings-page-shell__title-wrap customer-detail-header">
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexWrap: "wrap", rowGap: 1 }}>
          <IconButton onClick={() => navigate("/operators")} aria-label="Go back" edge="start" size="medium">
            <ArrowBackIcon sx={{ fontSize: 24 }} />
          </IconButton>
          <Typography variant="h5" className="settings-page-shell__title" component="h1">
            {operator.companyName}
          </Typography>
          <GridPillChip
            pillVariant={operator.status === "active" ? "breeze" : "critical"}
            label={operator.status === "active" ? "ACTIVE" : "INACTIVE"}
            size="small"
          />
        </Stack>
      </div>

      <div className="settings-page-shell__tabs customer-detail-tabs">
        {DETAIL_TABS.map((tab) => (
          <PivotTab
            key={tab.id}
            label={tab.label}
            active={activeTab === tab.id}
            onActivate={() => handleTabChange(tab.id)}
            className="settings-page-shell__pivot-tab"
          />
        ))}
      </div>

      <div className="users-page__main">
        {activeTab === "overview" ? (
          <OperatorDetailOverview operator={operator} />
        ) : activeTab === "locations" ? (
          <OperatorDetailLocations operator={operator} />
        ) : (
          <OperatorDetailCustomers operator={operator} />
        )}
      </div>
    </div>
  );
}

function parseTabParam(value: string | null): OperatorDetailTab {
  if (value === "locations" || value === "customers") {
    return value;
  }
  return "overview";
}